import { newFlow } from "../../common/factory/flow.factory";
import {
  DELETE_FLOW,
  GET_FLOWS,
  MODIFIED_SELECTED_FLOW,
  SAVE_FLOW,
  SAVE_FLOW_ONLY,
  SET_SAVED,
  CREATE_FLOW,
  GET_LOGS,
  GET_NEXT_LOG_PAGE,
  PREVIEW_REQUEST,
  RESOLVE_REQUEST,
  CLEAR_LOG,
  SET_URL,
  SET_RENDER,
  GET_LOG_ITEM,
  GET_ATTACHMENT_LOGS,
  CLEAR_ATTACHMENT_LOGS,
  SET_FLOW_EDIT_LOCK,
  SET_FLOW_RUNNER_STATUS,
} from "../actions/types";
import { isEqual } from "lodash";

const initialState = {
  flows: [],
  flow: newFlow(),
  flowsMetadata: {
    lastEvaluatedKey: null,
    count: 0,
  },
  saved: true,
  logs: [],
  attachmentLogs: null,
  showPreview: false,
  resolvedRequest: null,
  currentBody: null,
  currentUrl: null,
  render: null,
  locked: false,
  flowRunnerStatuses: {},
};

const merge = (flows, flow) => {
  if (flows) {
    const filteredFlows = flows.filter((f) => f.id !== flow.id);
    return [...filteredFlows, flow];
  }
  return [flow];
};

const sort = (flows) => {
  return flows.sort(function (a, b) {
    return new Date(a.created) - new Date(b.created);
  });
};

const normalizeLogPayload = (logs) => {
  if (!logs || Array.isArray(logs)) {
    return { result: [], currentTime: Date.now() };
  }

  return {
    ...logs,
    result: Array.isArray(logs.result) ? logs.result : [],
    currentTime: logs.currentTime || Date.now(),
  };
};

const getLogIdentifier = (log) => {
  if (!log || typeof log !== "object") {
    return undefined;
  }

  return (
    log.id ||
    (log.flowId && log.timestamp ? `${log.flowId}-${log.timestamp}` : undefined) ||
    log.timestamp ||
    log.scope?.key ||
    JSON.stringify(log)
  );
};

const isSameLogContext = (currentPage, newPage) => {
  if (!currentPage || !newPage) {
    return false;
  }

  const currentFlowId = currentPage.flowId ?? null;
  const nextFlowId = newPage.flowId ?? null;
  const currentActionId = currentPage.actionId ?? null;
  const nextActionId = newPage.actionId ?? null;

  return currentFlowId === nextFlowId && currentActionId === nextActionId;
};

const mergeLogEntries = (currentPage, newPage) => {
  if (!newPage) {
    return normalizeLogPayload(currentPage);
  }

  const incoming = normalizeLogPayload(newPage);

  if (!currentPage || Array.isArray(currentPage) || !isSameLogContext(currentPage, incoming) || !Array.isArray(currentPage?.result)) {
    return incoming;
  }

  const existing = normalizeLogPayload(currentPage);
  const seen = new Set();

  const mergedResult = [...existing.result, ...incoming.result].filter((log) => {
    const identifier = getLogIdentifier(log) ?? JSON.stringify(log);
    if (seen.has(identifier)) {
      return false;
    }

    seen.add(identifier);
    return true;
  });

  mergedResult.sort((a, b) => (Number(b?.timestamp) || 0) - (Number(a?.timestamp) || 0));

  return {
    ...existing,
    ...incoming,
    result: mergedResult,
  };
};

const enrichLogItem = (logItem, logPage) => {
  // Guard clauses for null/undefined checks
  if (!logItem || !logPage || !Array.isArray(logPage.result)) {
    return logPage;
  }

  // Find log item in list of logs
  const index = logPage.result.findIndex((log) => log.scope?.key === logItem.logKey);

  // Enrich log with new data if found
  if (index !== -1) {
    const existingLog = logPage.result[index];
    const {
      request,
      response,
      currentPayload: payload,
      scope,
      requestHeaders,
      responseHeaders,
      script,
      url,
      errorText,
      verboseLog,
    } = logItem.data;

    // Updating existing log
    Object.assign(existingLog, {
      request,
      response,
      payload,
      scope,
      requestHeaders,
      responseHeaders,
      script, // only relevant for script actions
      url: url ? url : existingLog?.url,
      errorText: errorText ? errorText : existingLog?.errorText,
      verboseLog: verboseLog ? verboseLog : existingLog?.verboseLog,
    });

    //add has loaded log to scope to determine if it shall try to fetch log again
    existingLog.s3LogLoaded = true;

    logPage.currentTime = Date.now();
  }

  // Return a shallow copy if immutability is needed
  return { ...logPage, result: [...logPage.result] };
};

export default function (state = initialState, action) {
  switch (action.type) {
    case GET_FLOWS:
      // Deep compare and merge only if objects in action.payload.result are not already in state.flows
      const mergedFlows = action.payload.result.reduce((acc, payloadFlow) => {
        // Check if the payloadFlow already exists in state.flows
        const existingFlowIndex = state.flows.findIndex((flow) => isEqual(flow, payloadFlow));
        if (existingFlowIndex === -1) {
          // If not found, merge it into the state.flows
          return [...acc, payloadFlow];
        } else {
          // If found, keep the existing state.flows as it is
          return acc;
        }
      }, state.flows);

      return {
        ...state,
        flows: sort([...mergedFlows]),
        flowsMetadata: {
          count: action.payload.count,
          lastEvaluatedKey: action.payload.lastEvaluatedKey,
        },
      };

    case MODIFIED_SELECTED_FLOW:
      return {
        ...state,
        flow: action.payload,
      };
    case SET_SAVED:
      return {
        ...state,
        saved: action.payload,
      };
    case CREATE_FLOW:
      return {
        ...state,
        flow: JSON.parse(JSON.stringify(action.payload)),
        flows: sort(merge(state.flows, action.payload)),
        saved: true,
      };
    case SAVE_FLOW:
      return {
        ...state,
        flow: JSON.parse(JSON.stringify(action.payload)),
        flows: sort(merge(state.flows, action.payload)),
        saved: true,
      };
    case SAVE_FLOW_ONLY:
      return {
        ...state,
        flows: sort(merge(state.flows, action.payload)),
        saved: true,
      };
    case DELETE_FLOW:
      return {
        ...state,
        flow: newFlow(),
        flows: state.flows.filter((f) => f.id !== action.payload.id),
        saved: true,
      };
    case GET_LOG_ITEM:
      return {
        ...state,
        logs: enrichLogItem(action.payload, state.logs),
      };
    case GET_LOGS:
      return {
        ...state,
        logs: mergeLogEntries(state.logs, action.payload),
      };
    case GET_NEXT_LOG_PAGE:
      return {
        ...state,
        logs: mergeLogEntries(state.logs, action.payload),
      };
    case PREVIEW_REQUEST:
      return {
        ...state,
        showPreview: action.payload,
      };
    case RESOLVE_REQUEST:
      return {
        ...state,
        resolvedRequest: action.payload,
      };
    case CLEAR_LOG:
      return {
        ...state,
        logs: action.payload,
      };
    case GET_ATTACHMENT_LOGS:
      return {
        ...state,
        attachmentLogs: action.payload,
      };
    case CLEAR_ATTACHMENT_LOGS:
      return {
        ...state,
        attachmentLogs: null,
      };
    case SET_URL:
      return {
        ...state,
        currentUrl: action.payload,
      };
    case SET_RENDER:
      return {
        ...state,
        render: action.payload,
      };
    case SET_FLOW_EDIT_LOCK:
      return {
        ...state,
        locked: action.payload,
      };
    case SET_FLOW_RUNNER_STATUS:
      return {
        ...state,
        flowRunnerStatuses: {
          ...state.flowRunnerStatuses,
          [action.payload.flowId]: action.payload.statuses,
        },
      };
    default:
      return state;
  }
}
